import { ITheme } from 'survey-core';

/* eslint-disable no-unused-vars */
export enum CreditApplicationStatus {
  APPROVED = 'APPROVED',
  APPROVED_WITH_CHANGES = 'APPROVED_WITH_CHANGES',
  REJECTED = 'REJECTED',
  IN_PROGRESS = 'IN_PROGRESS',
  READY_FOR_REVIEW = 'READY_FOR_REVIEW',
  AWAITING_SIGNATURE = 'AWAITING_SIGNATURE',
  SUBMITTED = 'SUBMITTED',
  AWAITING_THIRD_PARTIES = 'AWAITING_THIRD_PARTIES',
}

export enum ExternalProvider {
  CREDITSAFE = 'creditsafe',
}

export enum CreditApplicationThirdPartyStatus {
  NOT_SENT = 'NOT_SENT',
  PENDING = 'PENDING',
  COMPLETE = 'COMPLETE',
}

export enum Sentiment {
  POSITIVE = 'positive',
  NEGATIVE = 'negative',
  NEUTRAL = 'neutral',
}

export enum UserType {
  INITIAL_USER = 'initial_user',
  SIGNER = 'signer',
}

export enum FraudCheckSeverity {
  THREAT = 'THREAT',
  WARNING = 'WARNING',
  SUCCESS = 'SUCCESS',
}

export enum ThirdPartyType {
  TRADE_REFERENCE = 'trade_reference',
  PERSONAL_GUARANTOR = 'personal_guarantor',
}

export enum FraudCheckType {
  EMAIL_FREE = 'EMAIL_FREE',
  EMAIL_DISPOSABLE = 'EMAIL_DISPOSABLE',
  EMAIL_PASSED_SECURITY = 'EMAIL_PASSED_SECURITY',
  IP_PROXY = 'IP_PROXY',
  IP_THREAT = 'IP_THREAT',
  IP_INVALID = 'IP_INVALID',
  IP_PASSED_SECURITY = 'IP_PASSED_SECURITY',
  EMAIL_DOMAIN_INFO_MISSING = 'EMAIL_DOMAIN_INFO_MISSING',
  EMAIL_DOMAIN_MATCH_FAIL = 'EMAIL_DOMAIN_MATCH_FAIL',
  EMAIL_DOMAIN_MATCH_PASS = 'EMAIL_DOMAIN_MATCH_PASS',
  IP_LOCATION_INFO_MISSING = 'IP_LOCATION_INFO_MISSING',
  IP_LOCATION_COUNTRY_MATCH_FAIL = 'IP_LOCATION_COUNTRY_MATCH_FAIL',
  IP_LOCATION_STATE_MATCH_FAIL = 'IP_LOCATION_STATE_MATCH_FAIL',
  IP_LOCATION_STATE_MATCH_PASS = 'IP_LOCATION_STATE_MATCH_PASS',
  IP_LOCATION_POSTAL_MATCH = 'IP_LOCATION_POSTAL_MATCH',
  IP_MATCH_WITH_TRADE_REFERENCE = 'IP_MATCH_WITH_TRADE_REFERENCE',
  IP_MATCH_WITH_INITIAL_USER = 'IP_MATCH_WITH_INITIAL_USER',
  IP_MATCH_WITH_SIGNER = 'IP_MATCH_WITH_SIGNER',
  EMAIL_DOMAIN_MATCH_WITH_TRADE_REFERENCE = 'EMAIL_DOMAIN_MATCH_WITH_TRADE_REFERENCE',
  EMAIL_DOMAIN_MATCH_WITH_INITIAL_USER = 'EMAIL_DOMAIN_MATCH_WITH_INITIAL_USER',
  EMAIL_DOMAIN_MATCH_WITH_SIGNER = 'EMAIL_DOMAIN_MATCH_WITH_SIGNER',
}

/* eslint-enable no-unused-vars */

export type CreditApplicationSubmissionDecisionStatus =
  | CreditApplicationStatus.APPROVED
  | CreditApplicationStatus.APPROVED_WITH_CHANGES
  | CreditApplicationStatus.REJECTED;

export interface ThemeColors {
  header: string;
  sidebar: string;
}

export interface Theme {
  logoUrl: string
  colors: ThemeColors
}

export interface ThemeColorsInput {
  header: string
  sidebar: string
}

export interface ThemeInput {
  logoUrl: string
  colors: ThemeColorsInput
}

export interface SurveyPage {
  title: string
  elements: Record<string, any>[]
  [key: string]: any
}

export interface Survey {
  pages: SurveyPage[]
  [key: string]: any
}

export interface SurveyPageInput {
  title: string
  elements: Record<string, any>[]
  [key: string]: any
}

export interface SurveyInput {
  pages: SurveyPageInput[]
  [key: string]: any
}

export interface CreditApplicationDetails {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  role: string;
  department?: string;
}

export interface CreditApplicationVerificationToken {
  hashedToken: string;
  expiresAt: string;
  isVerified: boolean;
}

interface LocationInfo {
  countryCode?: string | null;
  region?: string | null;
  postalCode?: string | null;
}

export interface CreditApplicationSignerDetails {
  firstName: string;
  lastName: string;
  phone: string;
  role: string;
  department?: string;
}

export interface CreditApplicationSigner {
  email: string;
  isInitialUser: boolean;
  ipAddress?: string | null;
  location?: LocationInfo | null;
  details?: CreditApplicationSignerDetails;
  verificationToken?: CreditApplicationVerificationToken;
}

export interface CreditApplicationThirdPartyDetails {
  companyName?: string | null;
  companyAddress: string;
  contactName: string;
  contactEmail?: string | null;
  contactPhone: string;
}

export interface CreditApplicationMetrics {
  totalApplications: number | null;
  totalApplicationsTrend: number | null;
  percentApproved: number | null;
  percentApprovedTrend: number | null;
  creditApproved: number | null;
}

export interface SentimentStyle {
  background: string;
  border: string;
  color: string;
}

export interface AdditionalThirdParty {
  numberOfContacts: number;
  userType?: UserType | null;
  requestDate: string;
}

export interface CreditApplicationThirdPartyEmailSent {
  lastSentDate?: string | null;
  total?: number;
}

export interface FraudCheckObject {
  type: FraudCheckType;
  severity: FraudCheckSeverity;
  context: {
    fieldPath: string;
    value: string | null;
    expectedValues?: string[] | null;
  };
}

export interface CreditApplicationThirdParty {
  id: string;
  submissionId: string;
  type: ThirdPartyType;
  details: CreditApplicationThirdPartyDetails;
  emailSent?: CreditApplicationThirdPartyEmailSent | null;
  sentiment?: Sentiment | null;
  ipAddress?: string | null;
  fraudChecks?: FraudCheckObject[] | null;
  responses?: Record<string, any> | null;
  status: CreditApplicationThirdPartyStatus;
  submittedDate?: string | null;
  created: string;
}

export interface CreditApplicationSubmission {
  id: string;
  applicationSettingsId: string;
  publicId: string;
  details: CreditApplicationDetails;
  responses: Record<string, any>;
  verificationToken?: CreditApplicationVerificationToken | null;
  signer?: CreditApplicationSigner | null;
  ipAddress: string | null;
  location?: LocationInfo | null;
  status: CreditApplicationStatus;
  created: string;
  modified: string;
  submittedDate?: string | null;
  accountId?: string | null;
  fraudChecks?: FraudCheckObject[] | null;
  approvedCreditLimit?: number | null;
  approvedCreditLimitCurrency?: string | null;
  decisionMakerUserId?: string | null;
  decisionDate?: string | null;
  thirdParties?: CreditApplicationThirdParty[] | null;
  additionalThirdParties?: Partial<Record<ThirdPartyType, AdditionalThirdParty>> | null;
  terms?: {
    paymentTermsDays: number | null;
    discount: number | null;
    discountDays: number | null;
    latePayPenalty: number | null;
  } | null;
  decisionReasoning?: string | null;
}

export interface ThirdPartyCounts {
  total: number;
  emailSent: number;
  submitted: number;
}

export interface CreditApplicationGridSubmission {
  id: string;
  company: string;
  applicationTitle: string;
  submittedDate?: string | null;
  modified: string;
  status: CreditApplicationStatus;
  creditLimit?: number | null;
  paymentTerms?: string | null;
  groupId?: string | null;
  accountId?: string | null;
  tradeReferenceCounts: ThirdPartyCounts;
  personalGuarantorCounts: ThirdPartyCounts;
}

export interface StatusCount {
  status: CreditApplicationStatus;
  count: number;
}

export interface ThirdPartySurvey extends Survey {
  title?: string;
}

export interface ThirdPartySurveyInput extends SurveyInput {
  title?: string;
}

export interface CreditApplicationSettings {
  id: string
  orgId: string
  groupId?: string
  urlSlug: string
  entityName?: string
  isActive: boolean
  version: number
  theme: Theme
  surveysTheme?: ITheme
  survey: Survey
  thirdPartySurveys?: Partial<Record<ThirdPartyType, ThirdPartySurvey>> | null;
  autoSendTradeReferences?: boolean;
  autoSendPersonalGuarantors?: boolean;
  created: string
  modified: string
}

export interface CreditAppWithSubmission {
  settings: CreditApplicationSettings;
  submission: CreditApplicationSubmission;
}

export type ThirdPartySurveysRecord = Partial<Record<ThirdPartyType, ThirdPartySurvey>> | undefined | null;
